import { IIssueMachineHealthResponse } from '@kcftech/prometheustypescriptapiclient';
import { EmptyState } from 'components/empty_state/EmptyState';
import { InfiniteScroll } from 'components/infinite_scroll/InfiniteScroll';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import { LocationIssuesRow } from 'pages/Location/issues/LocationIssuesRow';
import {
    gridColumnSizes,
    IssuesSortBar,
    IssuesTable,
    PopupWrapper,
    SortBarLayoutOverrides,
    SortItemOverrides,
    TableContent,
} from 'pages/Location/issues/styled';
import { memo } from 'react';
import { InfoPopup } from 'shared/info_popup/InfoPopup';
import { excludeNullAndUndefined } from 'utils/array';

const sortBarConfig = excludeNullAndUndefined([
    {
        id: 'issue-number',
        title: 'Issue #',
        field: 'IssueNumber',
        gridTemplateColumn: gridColumnSizes.issueNumber,
    },
    {
        id: 'severity',
        title: 'Severity',
        field: 'Severity',
        gridTemplateColumn: gridColumnSizes.severity,
    },
    {
        id: 'priority',
        title: (
            <span>
                Priority
                <PopupWrapper>
                    <InfoPopup title="Priority">
                        Priority correlates to how critical the asset is to the overall
                        health of your plant
                    </InfoPopup>
                </PopupWrapper>
            </span>
        ),
        field: 'Priority',
        gridTemplateColumn: gridColumnSizes.priority,
    },
    {
        id: 'status',
        title: 'Status',
        field: 'IssueStatus.StatusType',
        gridTemplateColumn: gridColumnSizes.status,
    },
    {
        id: 'fault-type',
        title: 'Fault Type',
        gridTemplateColumn: gridColumnSizes.faultType,
    },
    {
        id: 'fault-location',
        title: 'Fault Location',
        field: 'HierarchyName',
        gridTemplateColumn: gridColumnSizes.faultLocation,
    },
    {
        id: 'date-created',
        title: 'Created',
        field: 'Created',
        gridTemplateColumn: gridColumnSizes.created,
    },
    {
        id: 'last-updated',
        title: 'Last Updated',
        field: 'LastActivity',
        gridTemplateColumn: gridColumnSizes.lastUpdated,
    },
    {
        id: 'actions',
        title: '',
        gridTemplateColumn: gridColumnSizes.actions,
    },
]);

type LocationIssuesTableProps = Readonly<{
    results?: IIssueMachineHealthResponse[];
    sort: DataSourceSort[];
    onSort: (sort: DataSourceSort | undefined) => void;
    isLoading: boolean;
    hasNext?: boolean;
    onLoadMore: () => void;
}>;

function LocationIssuesTable({
    results,
    sort,
    onSort,
    isLoading,
    hasNext,
    onLoadMore,
}: LocationIssuesTableProps) {
    return (
        <IssuesTable>
            <IssuesSortBar
                id="location-issues-sort"
                itemsConfig={sortBarConfig}
                sort={sort}
                onSort={onSort}
                layoutStyles={SortBarLayoutOverrides}
                sortItemStyles={SortItemOverrides}
            />
            <TableContent>
                <SpinnerArea isLoading={isLoading}>
                    <InfiniteScroll
                        onLoadMore={onLoadMore}
                        isLoading={isLoading}
                        enabled={hasNext ?? false}
                        className="position-relative"
                    >
                        {results?.length === 0 ? (
                            <EmptyState message="No recent active issues found" />
                        ) : (
                            results?.map((issue) => (
                                <LocationIssuesRow key={issue.Id} issue={issue} />
                            ))
                        )}
                    </InfiniteScroll>
                </SpinnerArea>
            </TableContent>
        </IssuesTable>
    );
}

const memoLocationIssuesTable = memo(LocationIssuesTable);
export { memoLocationIssuesTable as LocationIssuesTable };
